import { useEffect, useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { isKnownBrand } from '../utils/brandMatch'
import { BrandCell } from './BrandCell'
import { suggestPartNumbers } from '../../../shared/api/partCatalog.api'
import { NoteCell } from './NoteCell'
import { QuantityCell } from './QuantityCell'
import { SortableItemRow } from './SortableItemRow'
import { ItemsTableFooter } from './ItemsTableFooter'
import { PartNumberCell } from './PartNumberCell'
import { namingTextClass, namingTitle } from './itemsTable.naming'
import {
  COL_ORDER,
  DEFAULT_COL_WIDTHS,
  MATRIX_LABELS,
  MATRIX_OPTIONS,
  PRIORITY_LABELS,
  type ColKey,
  type ItemRow,
} from './itemsTable.types'

// Типы и константы строки — в itemsTable.types.ts; реэкспорт сохраняет старые импорты.
export {
  MATRIX_LABELS,
  MATRIX_OPTIONS,
  PRIORITY_LABELS,
  makeEmptyRow,
  makeRowUid,
  type ItemRow,
  type MatrixOption,
} from './itemsTable.types'

// ─── Props ────────────────────────────────────────────────────────────────────

interface TableProps {
  items: ItemRow[]
  onChange: (idx: number, field: ColKey | 'checked', value: string | number | boolean) => void
  onDelete: (idx: number) => void
  onAdd: () => void
  onClearAll: () => void
  /** Перестановка позиций через DnD. Идексы — текущие в массиве items. */
  onReorder?: (fromIdx: number, toIdx: number) => void
  knownBrands?: Set<string>
  /** Клик по ⚠ скроллит к строке в верхнем блоке «Результат распознавания». */
  onJumpToSourceLine?: (lineNum: number) => void
  /** UID'ы строк, которые нужно временно подсветить (например, дубли при клике
   *  по счётчику «Дубли» в правой панели статистики). */
  highlightUids?: Set<string>
  /** Режим подсветки — задаёт цвет рамки/фона. */
  highlightMode?: 'none' | 'dupes' | 'errors' | 'attention'
}

// ─── Компонент ────────────────────────────────────────────────────────────────

export function ItemsTable({
  items,
  onChange,
  onDelete,
  onAdd,
  onClearAll,
  onReorder,
  knownBrands,
  onJumpToSourceLine,
  highlightUids,
  highlightMode,
}: TableProps) {
  const highlightClass =
    highlightMode === 'errors'
      ? 'ring-2 ring-red-500 ring-inset'
      : highlightMode === 'attention'
        ? 'ring-2 ring-orange-400 ring-inset bg-orange-50'
        : 'ring-2 ring-amber-400 ring-inset bg-amber-50'
  // PointerSensor с activationConstraint distance=4 — drag начинается только
  // после небольшого сдвига, иначе клик в drag-handle конфликтовал бы с
  // фокусом по соседним инпутам.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !onReorder) return
    const fromIdx = items.findIndex((it) => it.uid === active.id)
    const toIdx = items.findIndex((it) => it.uid === over.id)
    if (fromIdx === -1 || toIdx === -1) return
    onReorder(fromIdx, toIdx)
  }

  const cellMap = useRef<Map<string, HTMLElement>>(new Map())
  const [colWidths, setColWidths] = useState<Record<ColKey, number>>(DEFAULT_COL_WIDTHS)
  const resizeState = useRef<{ col: ColKey; startX: number; startW: number } | null>(null)

  // Автоподсказка partNumber из каталога (#25). Один общий datalist обновляется
  // при вводе в любой активной строке. Debounce 400мс, минимум 2 символа,
  // AbortController на предыдущий pending запрос — отрезает лишний трафик
  // при быстром наборе длинных описаний и снимает нагрузку с rate-limit.
  const [suggestions, setSuggestions] = useState<Array<{ partNumber: string; brand: string }>>([])
  const [suggestQuery, setSuggestQuery] = useState('')
  useEffect(() => {
    if (suggestQuery.trim().length < 2) {
      setSuggestions([])
      return
    }
    const abort = new AbortController()
    const timer = setTimeout(() => {
      void suggestPartNumbers(suggestQuery, 10, abort.signal)
        .then(setSuggestions)
        .catch(() => {
          // AbortError при отмене предыдущего запроса — это норма, не сбрасываем
          // подсказки. Сетевые ошибки тоже — лучше показать старый список, чем пустой.
        })
    }, 400)
    return () => {
      clearTimeout(timer)
      abort.abort()
    }
  }, [suggestQuery])

  const reg = (el: HTMLElement | null, r: number, c: number) => {
    const key = `${r}-${c}`
    if (el) cellMap.current.set(key, el)
    else cellMap.current.delete(key)
  }

  const focusCell = (r: number, c: number) => {
    cellMap.current.get(`${r}-${c}`)?.focus()
  }

  const handleKey = (e: React.KeyboardEvent, r: number, c: number) => {
    // Индекс последней НАВИГИРУЕМОЙ ячейки (reg 0..5: партномер, бренд, кол-во,
    // ед., заметка, приоритет). Матрица — <select>, «Наименование» — read-only,
    // в reg-навигацию не входят, поэтому это не COL_ORDER.length-1.
    const lastCol = 5
    if (e.key === 'Enter') {
      e.preventDefault()
      if (r < items.length - 1) {
        focusCell(r + 1, c)
      } else {
        onAdd()
        requestAnimationFrame(() => focusCell(r + 1, 0))
      }
    } else if (e.key === 'Tab' && !e.shiftKey && c === lastCol) {
      e.preventDefault()
      if (r < items.length - 1) {
        focusCell(r + 1, 0)
      } else {
        onAdd()
        requestAnimationFrame(() => focusCell(r + 1, 0))
      }
    } else if (e.key === 'Delete' && e.ctrlKey) {
      e.preventDefault()
      onDelete(r)
      requestAnimationFrame(() => focusCell(Math.min(r, items.length - 2), c))
    }
  }

  const startResize = (col: ColKey, e: React.MouseEvent) => {
    e.preventDefault()
    resizeState.current = { col, startX: e.clientX, startW: colWidths[col] }
    const onMove = (ev: MouseEvent) => {
      // Захватываем snapshot в локальную переменную: setColWidths внутри
      // батчится React-ом и может выполниться уже после onUp(), когда
      // resizeState.current = null. Без snapshot'а получали null.col → краш.
      const state = resizeState.current
      if (!state) return
      const delta = ev.clientX - state.startX
      const newW = Math.max(40, state.startW + delta)
      setColWidths((prev) => ({ ...prev, [state.col]: newW }))
    }
    const onUp = () => {
      resizeState.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const cellClass =
    'w-full px-1.5 py-1 text-xs border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded select-text'

  // Дубли по нормализованному (партномер|бренд) — чтобы показать текст «дубль»
  // прямо в строке (не только цветом в панели статистики).
  const dupCount = new Map<string, number>()
  for (const it of items) {
    const pn = it.partNumber.trim().toUpperCase()
    if (!pn || pn === '???') continue
    const key = `${pn}|${it.brand.trim().toUpperCase()}`
    dupCount.set(key, (dupCount.get(key) ?? 0) + 1)
  }
  const isDuplicate = (it: ItemRow): boolean => {
    const pn = it.partNumber.trim().toUpperCase()
    if (!pn || pn === '???') return false
    return (dupCount.get(`${pn}|${it.brand.trim().toUpperCase()}`) ?? 0) > 1
  }

  const ResizableTh = ({
    col,
    label,
    className = '',
  }: {
    col: ColKey
    label: string
    className?: string
  }) => (
    <th
      style={{ width: colWidths[col] }}
      className={`relative text-left px-2 py-2 font-medium text-gray-600 overflow-hidden whitespace-nowrap ${className}`}
    >
      <span className="block overflow-hidden text-ellipsis pr-2">{label}</span>
      <div
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize group"
        onMouseDown={(e) => startResize(col, e)}
      >
        <div className="absolute right-0 top-1 bottom-1 w-px bg-gray-200 group-hover:bg-brand-400 group-hover:w-0.5 transition-colors" />
      </div>
    </th>
  )

  return (
    <div className="overflow-x-auto">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <table
          className="text-xs border-collapse"
          style={{
            tableLayout: 'fixed',
            width: Object.values(colWidths).reduce((a, b) => a + b, 0) + 22 + 28 + 32 + 28 + 'px',
          }}
        >
          <colgroup>
            <col style={{ width: 22 }} />
            <col style={{ width: 28 }} />
            <col style={{ width: 32 }} />
            {COL_ORDER.map((col) => (
              <col key={col} style={{ width: colWidths[col] }} />
            ))}
            <col style={{ width: 28 }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="w-5" />
              <th className="w-7 px-2 py-2 text-center">
                <input
                  type="checkbox"
                  checked={items.every((i) => i.checked)}
                  onChange={(e) =>
                    items.forEach((_, idx) => onChange(idx, 'checked', e.target.checked))
                  }
                  className="accent-brand-600"
                />
              </th>
              <th className="w-8 px-1 py-2 text-center font-medium text-gray-400 text-[10px]">№</th>
              <ResizableTh col="matrix" label="Матрица" />
              <ResizableTh col="partNumber" label="Part Number *" />
              <ResizableTh col="category" label="Наименование" />
              <ResizableTh col="brand" label="Бренд (Brand)" />
              <ResizableTh col="quantity" label="Кол-во (QTY)" />
              <ResizableTh col="unit" label="шт" />
              <ResizableTh col="note" label="Заметка" />
              <ResizableTh col="priority" label="Приоритет закупки" />
              <th className="w-7" />
            </tr>
          </thead>
          <tbody>
            <SortableContext items={items.map((i) => i.uid)} strategy={verticalListSortingStrategy}>
              {items.map((row, r) => {
                const warnTitle = row.needsManualReview
                  ? row.parseWarnings && row.parseWarnings.length > 0
                    ? row.parseWarnings.join('\n')
                    : 'Требует ручной проверки'
                  : undefined
                return (
                  <SortableItemRow
                    key={row.uid}
                    uid={row.uid}
                    id={row.lineNumber != null ? `item-row-${row.lineNumber}` : undefined}
                    className={`border-b border-gray-100 hover:bg-gray-50/50 ${!row.checked ? 'opacity-40' : ''} ${row.needsManualReview ? 'bg-red-50' : ''} ${highlightUids?.has(row.uid) ? highlightClass : ''}`}
                  >
                    <td className="px-2 py-0.5 text-center align-top pt-1.5">
                      <input
                        type="checkbox"
                        checked={row.checked}
                        onChange={(e) => onChange(r, 'checked', e.target.checked)}
                        className="accent-brand-600"
                      />
                    </td>

                    <td className="px-1 py-0.5 text-center text-[10px] text-gray-400 font-mono align-top pt-2">
                      <div className="flex items-center justify-center gap-0.5">
                        {row.lineNumber != null ? (
                          <button
                            type="button"
                            title="Перейти к исходной строке в блоке «Результат распознавания»"
                            onClick={() => onJumpToSourceLine?.(row.lineNumber as number)}
                            className="text-gray-400 hover:text-brand-600 hover:underline cursor-pointer select-none leading-none"
                          >
                            {row.lineNumber}
                          </button>
                        ) : (
                          <span />
                        )}
                        {row.needsManualReview && (
                          <button
                            type="button"
                            title={
                              warnTitle ??
                              'Требует ручной проверки — кликни, чтобы увидеть исходную строку'
                            }
                            onClick={() =>
                              row.lineNumber != null && onJumpToSourceLine?.(row.lineNumber)
                            }
                            className="text-amber-500 hover:text-amber-600 cursor-pointer select-none leading-none"
                          >
                            ⚠
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="py-0.5 border-r border-gray-100">
                      <select
                        value={row.matrix}
                        onChange={(e) => onChange(r, 'matrix', e.target.value)}
                        className="w-full px-1 py-1 text-xs border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded"
                      >
                        <option value="">— не выбрано —</option>
                        {MATRIX_OPTIONS.map((m) => (
                          <option key={m} value={m}>
                            {MATRIX_LABELS[m] ?? m}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td className="py-0.5 border-r border-gray-100 align-top">
                      <PartNumberCell
                        row={row}
                        suggestions={suggestions}
                        isDuplicate={isDuplicate(row)}
                        cellClass={cellClass}
                        onChange={(field, value) => onChange(r, field, value)}
                        onQuery={setSuggestQuery}
                        onRegister={(el) => reg(el, r, 0)}
                        onKeyDown={(e) => handleKey(e, r, 0)}
                      />
                    </td>

                    <td className="group relative py-0.5 border-r border-gray-100">
                      <input
                        value={row.category}
                        onChange={(e) => onChange(r, 'category', e.target.value)}
                        placeholder="наименование"
                        className={`${cellClass} ${namingTextClass(row.namingStatus)}`}
                      />
                      {/* Кастомный тултип вместо нативного title — появляется
                          мгновенно при наведении (браузерный ждёт 1-2 сек). */}
                      {namingTitle(row) && (
                        <div className="pointer-events-none absolute left-1 top-full z-50 mt-0.5 hidden w-max max-w-xs whitespace-normal rounded-md bg-gray-900 px-2 py-1 text-xs leading-snug text-white shadow-lg group-hover:block">
                          {namingTitle(row)}
                        </div>
                      )}
                    </td>

                    <td className="py-0.5 border-r border-gray-100">
                      <BrandCell
                        row={row}
                        known={!!knownBrands && isKnownBrand(knownBrands, row.brand)}
                        cellClass={cellClass}
                        onChange={(val) => {
                          onChange(r, 'brand', val)
                          if (knownBrands && isKnownBrand(knownBrands, val)) {
                            onChange(r, 'matrix', 'Производители')
                          }
                        }}
                        onRegister={(el) => reg(el, r, 1)}
                        onKeyDown={(e) => handleKey(e, r, 1)}
                      />
                    </td>

                    <td className="py-0.5 border-r border-gray-100">
                      <QuantityCell
                        value={row.quantity}
                        unit={row.unit}
                        onChange={(v) => onChange(r, 'quantity', v)}
                        onRegister={(el) => reg(el, r, 2)}
                        onKeyDown={(e) => handleKey(e, r, 2)}
                        cellClass={cellClass}
                      />
                    </td>

                    <td className="py-0.5 border-r border-gray-100">
                      <input
                        ref={(el) => reg(el, r, 3)}
                        value={row.unit}
                        onChange={(e) => onChange(r, 'unit', e.target.value)}
                        onKeyDown={(e) => handleKey(e, r, 3)}
                        placeholder="pcs"
                        className={cellClass}
                      />
                    </td>

                    {/* Поле заметки — textarea с авторесайзом (высота подгоняется
                    в useEffect в NoteCell по value, и на лету в onChange) */}
                    <td className="py-0.5 border-r border-gray-100">
                      <NoteCell
                        value={row.note}
                        onChange={(v) => onChange(r, 'note', v)}
                        onRegister={(el) => reg(el, r, 4)}
                        cellClass={cellClass}
                        onEnter={() => {
                          if (r < items.length - 1) {
                            focusCell(r + 1, 4)
                          } else {
                            onAdd()
                            requestAnimationFrame(() => focusCell(r + 1, 0))
                          }
                        }}
                        onCtrlDelete={() => {
                          onDelete(r)
                          requestAnimationFrame(() => focusCell(Math.min(r, items.length - 2), 4))
                        }}
                      />
                    </td>

                    <td className="py-0.5 border-r border-gray-100">
                      <select
                        ref={(el) => reg(el, r, 5)}
                        value={row.priority}
                        onChange={(e) => onChange(r, 'priority', e.target.value)}
                        onKeyDown={(e) => handleKey(e, r, 5)}
                        className="w-full px-1 py-1 text-xs border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded"
                      >
                        {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td className="px-1 py-0.5 text-center align-top pt-1.5">
                      <button
                        type="button"
                        onClick={() => onDelete(r)}
                        className="text-gray-300 hover:text-red-500 text-base leading-none"
                        tabIndex={-1}
                      >
                        ×
                      </button>
                    </td>
                  </SortableItemRow>
                )
              })}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>

      {/* Общий datalist для подсказки partNumber — браузер сам фильтрует по вводу */}
      <datalist id="partnumber-suggestions">
        {suggestions.map((s) => (
          <option key={`${s.partNumber}-${s.brand}`} value={s.partNumber}>
            {s.brand}
          </option>
        ))}
      </datalist>

      <ItemsTableFooter
        items={items}
        onAdd={onAdd}
        onClearAll={onClearAll}
        onChange={
          onChange as (idx: number, field: string, value: string | number | boolean) => void
        }
      />
    </div>
  )
}
